/**
 * Template images (organisation logo, uploaded pictures) for layout and PDF:
 * downloaded once, sized (layout needs the natural aspect synchronously) and
 * kept as embeddable bytes. PDF has no SVG images, so SVGs are rasterised to
 * a PNG large enough for print (≈ 600 dpi on a 10 cm wide logo).
 */

import type { ImageResource, PdfImageResource } from '@pointorama/print-engine';
import type { ImageSource } from 'shared-types';

const ORG_LOGO_KEY = 'org-logo';
const SVG_RASTER_LONG_SIDE_PX = 2400;

interface LoadedImage {
  resource: ImageResource;
  pdf: PdfImageResource;
  /** For SVG previews. */
  href: string;
}

export interface PrintImageStore {
  resolve(source: ImageSource): ImageResource | null;
  pdf(key: string): PdfImageResource | null;
  href(key: string): string | null;
  /** Releases object URLs. */
  dispose(): void;
}

function sniff(bytes: Uint8Array): 'png' | 'jpeg' | 'svg' | null {
  if (bytes.length >= 8 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) return 'png';
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'jpeg';
  const head = new TextDecoder().decode(bytes.subarray(0, 512)).trimStart().toLowerCase();
  return head.startsWith('<svg') || (head.startsWith('<?xml') && head.includes('<svg')) ? 'svg' : null;
}

function loadHtmlImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Image could not be decoded: ${url}`));
    image.src = url;
  });
}

async function rasterizeSvg(url: string): Promise<{ bytes: Uint8Array; width: number; height: number }> {
  const image = await loadHtmlImage(url);
  const naturalW = image.naturalWidth || 300;
  const naturalH = image.naturalHeight || 150;
  const factor = SVG_RASTER_LONG_SIDE_PX / Math.max(naturalW, naturalH);
  const width = Math.round(naturalW * factor);
  const height = Math.round(naturalH * factor);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Canvas 2D context unavailable');
  context.drawImage(image, 0, 0, width, height);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) throw new Error('SVG rasterisation failed');
  return { bytes: new Uint8Array(await blob.arrayBuffer()), width, height };
}

async function loadImage(key: string, url: string): Promise<LoadedImage | null> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Image download failed (${response.status})`);
  const bytes = new Uint8Array(await response.arrayBuffer());
  const kind = sniff(bytes);
  if (!kind) return null;
  const href = URL.createObjectURL(new Blob([bytes], { type: kind === 'svg' ? 'image/svg+xml' : `image/${kind}` }));
  if (kind === 'svg') {
    const raster = await rasterizeSvg(href);
    return { resource: { key, widthPx: raster.width, heightPx: raster.height }, pdf: { kind: 'png', bytes: raster.bytes }, href };
  }
  const image = await loadHtmlImage(href);
  return { resource: { key, widthPx: image.naturalWidth, heightPx: image.naturalHeight }, pdf: { kind, bytes }, href };
}

/**
 * Loads the logo and the template's images. Images that fail to load are
 * left out (their elements print empty) instead of failing the export.
 */
export async function loadPrintImages(input: { logoUrl: string | null; assets: readonly { id: string; url: string }[] }): Promise<PrintImageStore> {
  const wanted: { key: string; url: string }[] = [...(input.logoUrl ? [{ key: ORG_LOGO_KEY, url: input.logoUrl }] : []), ...input.assets.map((asset) => ({ key: `asset-${asset.id}`, url: asset.url }))];
  const images = new Map<string, LoadedImage>();
  const results = await Promise.allSettled(wanted.map(async ({ key, url }) => ({ key, image: await loadImage(key, url) })));
  for (const result of results) {
    if (result.status === 'fulfilled' && result.value.image) images.set(result.value.key, result.value.image);
    else if (result.status === 'rejected') console.warn('Print image could not be loaded', result.reason);
  }
  return {
    resolve: (source) => images.get(source.kind === 'orgLogo' ? ORG_LOGO_KEY : `asset-${source.assetId}`)?.resource ?? null,
    pdf: (key) => images.get(key)?.pdf ?? null,
    href: (key) => images.get(key)?.href ?? null,
    dispose: () => {
      for (const image of images.values()) URL.revokeObjectURL(image.href);
      images.clear();
    },
  };
}
